/**
 * Installations-Anleitung fuer die PWA (#309).
 *
 * Drei Vorgaben:
 *   1. Nur auf einem Mobilgeraet zeigen — am Schreibtisch waere die Anleitung falsch
 *      (dort gibt es kein Teilen-Symbol und keinen Startbildschirm).
 *   2. Wegklickbar, und zwar dauerhaft.
 *   3. Die Anleitung muss zu **Geraet und Browser** passen. „Tippe auf Teilen" ist unter
 *      Android schlicht verkehrt, und unter iOS liegt das Symbol je nach Browser woanders.
 *
 * Die Erkennung steht bewusst hier und nicht in der Komponente: So laesst sie sich gegen
 * echte User-Agent-Zeichenketten pruefen, ohne einen Browser zu starten.
 *
 * Grundsatz bei Unsicherheit: lieber den Weg nennen, der sicher funktioniert, als einen
 * Menuepunkt zu versprechen, den es im jeweiligen Browser vielleicht nicht gibt.
 */

export type InstallPlattform = 'ios' | 'android';

export interface InstallAnleitung {
  plattform: InstallPlattform;
  /** Stabiler Schluessel — fuer Tests, nicht fuer die Anzeige. */
  kennung: string;
  /** Wie der Browser in der Anleitung heisst. */
  browser: string;
  schritte: string[];
  /** Wird unter den Schritten als Fliesstext gezeigt. */
  hinweis?: string;
  /**
   * false = dieser Browser kann es nicht (zuverlaessig) selbst; die Schritte fuehren dann
   * ueber einen anderen Browser. Die Oberflaeche formuliert die Ueberschrift entsprechend.
   */
  direktMoeglich: boolean;
}

/** In-App-Browser (Instagram, Facebook, LinkedIn …) koennen grundsaetzlich nicht installieren. */
const IN_APP = /FBAN|FBAV|FB_IAB|Instagram|LinkedInApp|Line\/|Snapchat|Pinterest|TikTok|GSA\//i;

const istIos = (ua: string, maxTouchPoints: number): boolean => {
  if (/iPad|iPhone|iPod/i.test(ua)) return true;
  // iPadOS 13+ meldet sich als Macintosh. Ein Mac ohne Touchscreen tut das nicht.
  return /Macintosh/i.test(ua) && maxTouchPoints > 1;
};

const iosAnleitung = (ua: string): InstallAnleitung => {
  if (IN_APP.test(ua)) {
    return {
      plattform: 'ios',
      kennung: 'ios-inapp',
      browser: 'In-App-Browser',
      direktMoeglich: false,
      schritte: [
        'Unten oder oben rechts auf das Menü tippen (drei Punkte).',
        '„In Safari öffnen" wählen.',
        'Dann in Safari die Anleitung erneut aufrufen.',
      ],
      hinweis: 'Diese Seite ist gerade innerhalb einer anderen App geöffnet. Von dort lässt sich nichts auf dem Home-Bildschirm ablegen.',
    };
  }

  if (/CriOS/i.test(ua)) {
    return {
      plattform: 'ios',
      kennung: 'ios-chrome',
      browser: 'Chrome auf dem iPhone',
      direktMoeglich: false,
      schritte: [
        'Die Adresse dieser Seite kopieren.',
        'Safari öffnen und die Adresse dort einfügen.',
        'Unten in der Leiste auf das Teilen-Symbol tippen (Quadrat mit Pfeil nach oben).',
        'Nach unten wischen und „Zum Home-Bildschirm" wählen, dann „Hinzufügen".',
      ],
      hinweis: 'Chrome bietet den Punkt in seinem eigenen Teilen-Menü teils auch an — wenn du ihn dort findest, geht es genauso. Über Safari klappt es auf jedem iPhone.',
    };
  }

  if (/FxiOS|EdgiOS|OPiOS|OPT\//i.test(ua)) {
    const browser = /FxiOS/i.test(ua) ? 'Firefox auf dem iPhone'
      : /EdgiOS/i.test(ua) ? 'Edge auf dem iPhone'
        : 'Opera auf dem iPhone';
    return {
      plattform: 'ios',
      kennung: 'ios-anderer',
      browser,
      direktMoeglich: false,
      schritte: [
        'Die Adresse dieser Seite kopieren.',
        'Safari öffnen und die Adresse dort einfügen.',
        'Unten in der Leiste auf das Teilen-Symbol tippen (Quadrat mit Pfeil nach oben).',
        'Nach unten wischen und „Zum Home-Bildschirm" wählen, dann „Hinzufügen".',
      ],
      hinweis: 'Auf dem iPhone legt zuverlässig nur Safari eine App auf dem Home-Bildschirm ab.',
    };
  }

  return {
    plattform: 'ios',
    kennung: 'ios-safari',
    browser: 'Safari',
    direktMoeglich: true,
    schritte: [
      'Unten in der Leiste auf das Teilen-Symbol tippen (Quadrat mit Pfeil nach oben).',
      'In der Liste nach unten wischen bis „Zum Home-Bildschirm".',
      'Oben rechts auf „Hinzufügen" tippen.',
    ],
    hinweis: 'Danach startet die App vom Home-Bildschirm ohne Browserleiste — und du bleibst länger angemeldet.',
  };
};

const androidAnleitung = (ua: string): InstallAnleitung => {
  if (IN_APP.test(ua)) {
    return {
      plattform: 'android',
      kennung: 'android-inapp',
      browser: 'In-App-Browser',
      direktMoeglich: false,
      schritte: [
        'Oben rechts auf das Menü tippen (drei Punkte).',
        '„In Chrome öffnen" bzw. „Im Browser öffnen" wählen.',
        'Dann dort die Anleitung erneut aufrufen.',
      ],
      hinweis: 'Diese Seite ist gerade innerhalb einer anderen App geöffnet. Von dort lässt sich nichts installieren.',
    };
  }

  if (/SamsungBrowser/i.test(ua)) {
    return {
      plattform: 'android',
      kennung: 'android-samsung',
      browser: 'Samsung Internet',
      direktMoeglich: true,
      schritte: [
        'Unten rechts auf das Menü tippen (drei Striche).',
        '„Seite hinzufügen zu" wählen.',
        '„Startbildschirm" antippen und bestätigen.',
      ],
    };
  }

  if (/EdgA/i.test(ua)) {
    return {
      plattform: 'android',
      kennung: 'android-edge',
      browser: 'Edge',
      direktMoeglich: true,
      schritte: [
        'Unten in der Mitte auf das Menü tippen (drei Striche).',
        '„Zum Telefon hinzufügen" bzw. „App installieren" wählen.',
        'Mit „Installieren" bestätigen.',
      ],
    };
  }

  if (/OPR\/|OPX\/|Opera/i.test(ua)) {
    return {
      plattform: 'android',
      kennung: 'android-opera',
      browser: 'Opera',
      direktMoeglich: true,
      schritte: [
        'Unten rechts auf das Menü tippen.',
        '„Startbildschirm" bzw. „Zum Startbildschirm hinzufügen" wählen.',
        'Mit „Hinzufügen" bestätigen.',
      ],
    };
  }

  if (/Firefox|FxiOS/i.test(ua)) {
    return {
      plattform: 'android',
      kennung: 'android-firefox',
      browser: 'Firefox',
      direktMoeglich: true,
      schritte: [
        'Rechts neben der Adressleiste auf das Menü tippen (drei Punkte).',
        '„Zum Startbildschirm hinzufügen" wählen.',
        'Mit „Hinzufügen" bestätigen.',
      ],
    };
  }

  if (/Chrome|CriOS/i.test(ua)) {
    return {
      plattform: 'android',
      kennung: 'android-chrome',
      browser: 'Chrome',
      direktMoeglich: true,
      schritte: [
        'Oben rechts auf das Menü tippen (drei Punkte).',
        '„App installieren" bzw. „Zum Startbildschirm hinzufügen" wählen.',
        'Mit „Installieren" bestätigen.',
      ],
      hinweis: 'Wenn dir Chrome die Installation von selbst anbietet, geht es auch darüber.',
    };
  }

  return {
    plattform: 'android',
    kennung: 'android-unbekannt',
    browser: 'dein Browser',
    direktMoeglich: true,
    schritte: [
      'Das Menü des Browsers öffnen (meist drei Punkte oder drei Striche).',
      'Nach „App installieren" oder „Zum Startbildschirm hinzufügen" suchen.',
      'Die Auswahl bestätigen.',
    ],
  };
};

/**
 * Liefert die passende Anleitung — oder `null`, wenn hier gar nicht installiert wird
 * (Schreibtisch-Browser). `null` heisst fuer die Oberflaeche: nichts anzeigen.
 */
export const erkenneInstallAnleitung = (
  ua: string,
  maxTouchPoints = 0,
): InstallAnleitung | null => {
  if (!ua) return null;
  if (istIos(ua, maxTouchPoints)) return iosAnleitung(ua);
  if (/Android/i.test(ua)) return androidAnleitung(ua);
  return null;
};

/**
 * Laeuft die Anwendung bereits als installierte App? Dann waere der Hinweis Unsinn.
 * Gleiche Pruefung wie in `App.tsx` (`isInstalledPWA`) — iOS kennt `display-mode` in der
 * installierten App nicht zuverlaessig, deshalb zusaetzlich `navigator.standalone`.
 */
export const laeuftAlsApp = (): boolean => {
  if (typeof window === 'undefined') return false;
  if ((window.navigator as Navigator & { standalone?: boolean }).standalone === true) return true;
  if (typeof window.matchMedia !== 'function') return false;
  return ['standalone', 'fullscreen', 'minimal-ui', 'window-controls-overlay']
    .some(modus => window.matchMedia(`(display-mode: ${modus})`).matches);
};

export const INSTALL_HINWEIS_KEY = 'pwa_install_weggeklickt';

export const hinweisWeggeklickt = (): boolean => {
  try {
    return localStorage.getItem(INSTALL_HINWEIS_KEY) === '1';
  } catch {
    // Privates Fenster o. ae. — dann zeigen wir den Hinweis eben, statt zu scheitern.
    return false;
  }
};

export const hinweisWegklicken = (): void => {
  try {
    localStorage.setItem(INSTALL_HINWEIS_KEY, '1');
  } catch {
    /* nicht speicherbar — dann bleibt es bei dieser Sitzung */
  }
};

/**
 * `beforeinstallprompt` — der Ein-Tipp-Weg unter Android/Chromium.
 *
 * Das Ereignis feuert **frueh**, oft bevor React steht. Deshalb wird es beim Programmstart
 * mitgeschnitten (analog `installGlobalErrorReporting`) und nicht erst in der Komponente
 * abonniert; sonst waere es beim Rendern laengst verpufft. Safari und Firefox kennen es
 * nicht — dort bleibt es bei der Anleitung.
 */
export interface InstallPrompt {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let gemerkterPrompt: InstallPrompt | null = null;
const beobachter = new Set<(p: InstallPrompt | null) => void>();

const melden = () => beobachter.forEach(f => f(gemerkterPrompt));

export const installPromptMitschneiden = (): void => {
  if (typeof window === 'undefined') return;
  window.addEventListener('beforeinstallprompt', (e: Event) => {
    // Ohne das zeigt Chrome seinen eigenen Streifen — wir wollen den Zeitpunkt selbst wählen.
    e.preventDefault();
    gemerkterPrompt = e as unknown as InstallPrompt;
    melden();
  });
  window.addEventListener('appinstalled', () => {
    gemerkterPrompt = null;
    melden();
  });
};

export const holeInstallPrompt = (): InstallPrompt | null => gemerkterPrompt;

export const beobachteInstallPrompt = (cb: (p: InstallPrompt | null) => void): (() => void) => {
  beobachter.add(cb);
  return () => { beobachter.delete(cb); };
};

/** Nach einem angenommenen Prompt gibt es nichts mehr zu zeigen. */
export const installPromptVerbrauchen = (): void => {
  gemerkterPrompt = null;
  melden();
};
